<template>
  <Teleport to="body">
    <Transition name="slide-down">
      <div v-if="visible" class="sync-progress-bar" :class="stageClass">
        <div class="progress-content">
          <el-icon class="progress-icon" :size="16">
            <Loading v-if="isLoading" class="is-loading" />
            <CircleCheck v-else-if="isCompleted" />
            <CircleClose v-else-if="isFailed" />
            <DataAnalysis v-else />
          </el-icon>
          <span class="progress-task">{{ taskLabel }}</span>
          <span class="progress-message">{{ currentProgress?.message || '' }}</span>
          <el-progress
            v-if="isLoading"
            :percentage="Math.round(currentProgress?.progressPct || 0)"
            :stroke-width="4"
            :show-text="false"
            class="progress-bar"
          />
        </div>
        <el-button
          v-if="isCompleted || isFailed"
          link
          class="close-btn"
          @click="dismiss"
        >
          <el-icon><Close /></el-icon>
        </el-button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { Loading, CircleCheck, CircleClose, DataAnalysis, Close } from '@element-plus/icons-vue'
import { useSyncProgress } from '@/composables/useSyncProgress'

const { currentProgress } = useSyncProgress()

const visible = ref(false)
const dismissed = ref(false)
const autoHideTimer = ref<ReturnType<typeof setTimeout> | null>(null)

const isLoading = computed(() => {
  const stage = currentProgress.value?.stage
  return stage === 'FETCHING' || stage === 'SAVING' || stage === 'ETL' || stage === 'VALIDATING'
})

const isCompleted = computed(() => currentProgress.value?.stage === 'COMPLETED')
const isFailed = computed(() => currentProgress.value?.stage === 'FAILED')

const stageClass = computed(() => ({
  'stage-loading': isLoading.value,
  'stage-success': isCompleted.value,
  'stage-error': isFailed.value,
}))

const taskLabel = computed(() => {
  const code = currentProgress.value?.taskCode || ''
  const labels: Record<string, string> = {
    product_performance_t1: '产品表现',
    profit_report_t1: '利润报表',
    ad_sp_product_t1: 'SP广告',
    ad_sd_product_t1: 'SD广告',
    purchased_asin_sp_t1: '已购商品',
    inventory_t0: '库存',
    review_t1: 'Review',
    cost_stream_t1: '成本流水',
  }
  return labels[code] || code || '数据同步'
})

watch(currentProgress, (event) => {
  if (!event || dismissed.value) return
  visible.value = true

  if (autoHideTimer.value) {
    clearTimeout(autoHideTimer.value)
  }
  if (isCompleted.value || isFailed.value) {
    autoHideTimer.value = setTimeout(() => {
      visible.value = false
    }, 5000)
  }
}, { immediate: true })

function dismiss() {
  visible.value = false
  dismissed.value = true
}
</script>

<style lang="scss" scoped>
.sync-progress-bar {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 360px;
  max-width: 600px;
  padding: 10px 20px;
  border-radius: 0 0 10px 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(8px);

  &.stage-loading {
    background: rgba(64, 158, 255, 0.95);
    color: #fff;
  }
  &.stage-success {
    background: rgba(103, 194, 58, 0.95);
    color: #fff;
  }
  &.stage-error {
    background: rgba(245, 108, 108, 0.95);
    color: #fff;
  }
}

.progress-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.progress-icon {
  flex-shrink: 0;
}

.progress-task {
  font-weight: 600;
  font-size: 13px;
  white-space: nowrap;
}

.progress-message {
  font-size: 12px;
  opacity: 0.9;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.progress-bar {
  width: 80px;
  flex-shrink: 0;
}

.close-btn {
  color: rgba(255, 255, 255, 0.8);
  &:hover {
    color: #fff;
  }
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
}
.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateX(-50%) translateY(-100%);
  opacity: 0;
}
</style>
